/**
 * Kimi Keyword Booster — Moonshot Kimi K2.6 powered SEO tools.
 * Route: /tools/kimi-booster
 *
 * 3 tabs:
 *   1. Long-tail expander — seed → 20 keyword variations w/ intent labels
 *   2. Keyword clusterer  — paste 50-200 keywords → semantic clusters
 *   3. Topic ideator      — seed → blog post titles + angles + target keywords
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Zap,
  Layers,
  Lightbulb,
  Copy,
  Download,
  Loader2,
  Search,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/kimi`;

const INTENT_COLORS = {
  informational: "bg-blue-100 text-blue-800 border-blue-200",
  commercial: "bg-purple-100 text-purple-800 border-purple-200",
  transactional: "bg-emerald-100 text-emerald-800 border-emerald-200",
  navigational: "bg-neutral-100 text-neutral-800 border-neutral-200",
};

const TABS = [
  { id: "expand", label: "Long-tail expander", Icon: Zap, desc: "Seed → 20 buyer-intent keywords" },
  { id: "cluster", label: "Keyword clusterer", Icon: Layers, desc: "Group 50-200 keywords into topics" },
  { id: "topics", label: "Blog topic ideator", Icon: Lightbulb, desc: "Seed → clickable post ideas" },
];

// ── Expand panel ────────────────────────────────────────────────────
function ExpandPanel() {
  const [seed, setSeed] = useState("");
  const [count, setCount] = useState(20);
  const [lang, setLang] = useState("en");
  const [variations, setVariations] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!seed.trim()) return;
    setBusy(true); setVariations([]);
    try {
      const r = await axios.post(`${API}/expand`, {
        seed_keyword: seed, count, language: lang, intent_mix: true,
      });
      setVariations(r.data?.variations || []);
      toast.success(`Generated ${r.data?.variations?.length || 0} keywords`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Expansion failed");
    } finally { setBusy(false); }
  };

  const copyAll = () => {
    navigator.clipboard.writeText(variations.map(v => v.keyword).join("\n"));
    toast.success("Copied!");
  };

  const downloadCsv = () => {
    const rows = [["keyword", "intent", "length", "why"], ...variations.map(v => [v.keyword, v.intent, v.length, v.why])];
    const csv = rows.map(r => r.map(cell => `"${String(cell || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `keywords-${seed.replace(/\s+/g, "-").slice(0, 30)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-[1fr_130px_130px] gap-3">
        <Input
          value={seed}
          onChange={(e) => setSeed(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="Seed keyword (e.g. 'best coffee beans')"
          data-testid="kimi-expand-seed"
        />
        <label className="text-xs font-semibold text-neutral-600">
          Count
          <select value={count} onChange={(e) => setCount(parseInt(e.target.value, 10))}
            className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-2 text-sm" data-testid="kimi-expand-count">
            {[10, 15, 20, 30, 40, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Language
          <select value={lang} onChange={(e) => setLang(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-2 text-sm" data-testid="kimi-expand-lang">
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="hinglish">Hinglish</option>
            <option value="ta">Tamil</option>
            <option value="te">Telugu</option>
            <option value="bn">Bengali</option>
            <option value="mr">Marathi</option>
          </select>
        </label>
      </div>
      <Button onClick={run} disabled={busy || !seed.trim()} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="kimi-expand-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Expand keywords</>}
      </Button>

      {variations.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Badge className="bg-emerald-100 text-emerald-800 border-transparent">{variations.length} variations</Badge>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyAll} data-testid="kimi-expand-copy"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
              <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="kimi-expand-csv"><Download className="w-3 h-3 mr-1" /> CSV</Button>
            </div>
          </div>
          <div className="space-y-1.5" data-testid="kimi-expand-results">
            {variations.map((v, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors">
                <span className="text-xs text-neutral-400 font-mono w-6 pt-0.5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111111]">{v.keyword}</p>
                  {v.why && <p className="text-xs text-neutral-500 mt-0.5">{v.why}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {v.intent && <Badge className={`text-[10px] ${INTENT_COLORS[v.intent] || ""} border`}>{v.intent}</Badge>}
                  {v.length && <span className="text-[10px] text-neutral-400 uppercase font-semibold">{v.length}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Cluster panel ───────────────────────────────────────────────────
function ClusterPanel() {
  const [text, setText] = useState("");
  const [count, setCount] = useState(8);
  const [clusters, setClusters] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const keywords = text.split("\n").map(k => k.trim()).filter(Boolean);
    if (keywords.length < 3) { toast.error("Paste at least 3 keywords"); return; }
    if (keywords.length > 200) { toast.error("Max 200 keywords per run"); return; }
    setBusy(true); setClusters([]);
    try {
      const r = await axios.post(`${API}/cluster`, { keywords, cluster_count: count });
      setClusters(r.data?.clusters || []);
      setCoverage(r.data?.coverage || null);
      toast.success(`${r.data?.clusters?.length || 0} clusters generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clustering failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Textarea
        rows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"One keyword per line, e.g.\nbuy running shoes\nbest running shoes 2026\nhow to clean running shoes\nnike vs adidas running shoes\n…"}
        className="font-mono text-xs"
        data-testid="kimi-cluster-input"
      />
      <div className="grid sm:grid-cols-[1fr_180px] gap-3 items-end">
        <div className="text-xs text-neutral-500">
          {text.split("\n").filter(l => l.trim()).length} keywords · min 3 · max 200
        </div>
        <label className="text-xs font-semibold text-neutral-600">
          Target clusters
          <select value={count} onChange={(e) => setCount(parseInt(e.target.value, 10))}
            className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-2 text-sm" data-testid="kimi-cluster-count">
            {[3, 5, 8, 10, 12, 15].map(n => <option key={n} value={n}>{n} clusters</option>)}
          </select>
        </label>
      </div>
      <Button onClick={run} disabled={busy} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="kimi-cluster-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Clustering…</> : <><Layers className="w-4 h-4 mr-2" /> Cluster keywords</>}
      </Button>

      {clusters.length > 0 && (
        <div className="space-y-4" data-testid="kimi-cluster-results">
          {coverage && (
            <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-600 flex flex-wrap gap-4">
              <span><strong>{coverage.clustered_count}</strong>/{coverage.input_count} clustered</span>
              {coverage.missing?.length > 0 && <span className="text-orange-600">⚠ {coverage.missing.length} missed</span>}
              {coverage.duplicates?.length > 0 && <span className="text-red-600">⚠ {coverage.duplicates.length} duplicated</span>}
            </div>
          )}
          {clusters.map((c, i) => (
            <div key={i} className="border border-neutral-200 rounded-xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#111111]">{c.name}</h3>
                  {c.rationale && <p className="text-xs text-neutral-500 mt-1">{c.rationale}</p>}
                </div>
                <Badge className={`${INTENT_COLORS[c.intent] || ""} border`}>{c.intent}</Badge>
              </div>
              {c.pillar_keyword && (
                <div className="mb-3 text-xs">
                  <span className="text-neutral-500 uppercase font-semibold">Pillar:</span>
                  <span className="ml-2 font-semibold text-[#A855F7]">{c.pillar_keyword}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {(c.keywords || []).map((k, j) => (
                  <span key={j} className="px-2.5 py-1 bg-neutral-100 rounded-md text-xs font-medium text-neutral-700">{k}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Topics panel ────────────────────────────────────────────────────
function TopicsPanel() {
  const [seed, setSeed] = useState("");
  const [count, setCount] = useState(10);
  const [topics, setTopics] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!seed.trim()) return;
    setBusy(true); setTopics([]);
    try {
      const r = await axios.post(`${API}/related-topics`, { seed, count });
      setTopics(r.data?.topics || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ideation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-[1fr_130px] gap-3">
        <Input value={seed} onChange={(e) => setSeed(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="Seed topic (e.g. 'AI marketing for small businesses')"
          data-testid="kimi-topics-seed" />
        <select value={count} onChange={(e) => setCount(parseInt(e.target.value, 10))}
          className="h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="kimi-topics-count">
          {[5, 8, 10, 15, 20, 25].map(n => <option key={n} value={n}>{n} ideas</option>)}
        </select>
      </div>
      <Button onClick={run} disabled={busy || !seed.trim()} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="kimi-topics-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ideating…</> : <><Lightbulb className="w-4 h-4 mr-2" /> Generate ideas</>}
      </Button>

      {topics.length > 0 && (
        <div className="space-y-3" data-testid="kimi-topics-results">
          {topics.map((t, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-4 hover:border-[#A855F7] transition-colors">
              <div className="flex items-start justify-between gap-3 mb-1">
                <h4 className="font-semibold text-[#111111] text-sm">{t.title}</h4>
                {t.format && <span className="text-[10px] uppercase text-neutral-400 font-bold tracking-wider">{t.format}</span>}
              </div>
              {t.angle && <p className="text-xs text-neutral-600 mb-2">{t.angle}</p>}
              {t.target_keywords?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {t.target_keywords.map((k, j) => (
                    <span key={j} className="text-[10px] px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">{k}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function KimiBoosterPage() {
  const [tab, setTab] = useState("expand");
  const [health, setHealth] = useState(null);

  useEffect(() => {
    axios.get(`${API}/health`).then(r => setHealth(r.data)).catch(() => {});
  }, []);

  const ActivePanel = { expand: ExpandPanel, cluster: ClusterPanel, topics: TopicsPanel }[tab];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-transparent">🚀 Frontier LLM</Badge>
            <Badge className="bg-emerald-100 text-emerald-800 border-transparent">20× lower cost</Badge>
            {health?.configured && <Badge className="bg-neutral-100 text-neutral-700 border-transparent">Live</Badge>}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            MarkeeMix Keyword Booster
          </h1>
          <p className="text-neutral-600 mt-3 max-w-2xl text-base">
            256K-context frontier LLM built into MarkeeMix — long-tail expansion,
            semantic clustering, and blog topic ideation at ~20× lower cost than premium models.
            Perfect for large keyword lists and content ideation sprints.
          </p>
        </div>
      </section>

      <div className="border-b border-neutral-200 bg-white sticky top-16 z-10">
        <div className="max-w-5xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-4 border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active ? "border-[#A855F7] text-[#7C3AED]" : "border-transparent text-neutral-500 hover:text-neutral-800"
                }`} data-testid={`kimi-tab-${t.id}`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        <div className="mb-4 text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
          <Search className="w-3 h-3" /> {TABS.find(t => t.id === tab)?.desc}
        </div>
        <Card className="p-6 border-neutral-200 bg-white">
          <ActivePanel />
        </Card>

        <div className="mt-8 text-center text-xs text-neutral-500">
          Part of the <span className="text-[#7C3AED] font-semibold">MarkeeMix AI</span> stack · 256K context · JSON-mode structured outputs
        </div>
      </main>

      <PremiumFooter />
    </div>
  );
}
